/*
* @Author: Gang Jiang
* @Date: 2026-03-06 10:35:19
* @Last Modified By: Gang Jiang
* @Last Modified Time: 2026-05-21 17:06:24
* Description: Database table: influencer_marketing.finance_payment_dm_received
*/

<template>
  <div class="dm-received">
    <div class="filter">
      <a-form :model="filter">
        <a-row align="middle">
          <a-col :xs="24" :sm="12" :md="5">
            <a-form-item :labelCol="{ span: 8 }" label="Affiliate">
              <a-select v-model:value="filter.affiliateId" placeholder="Select" allowClear :options="affiliateOptions"
                :getPopupContainer="(triggerNode) => triggerNode.parentNode" />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="12" :md="7">
            <a-form-item :labelCol="{ span: 9 }" label="Payment Date">
              <a-range-picker
                v-model:value="filterDate"
                class="filter-date-picker"
                format="YYYY-MM-DD"
                :allowClear="true"
                :placeholder="['Start date', 'End date']"
                :disabled-date="disabledDate"
              />
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="12" :md="6">
            <a-form-item :labelCol="{ span: 8 }" label="Status">
              <a-checkbox-group v-model:value="filter.statusList" class="status-checkbox-group">
                <a-checkbox value="Pending">Pending</a-checkbox>
                <a-checkbox value="Confirmed">Confirmed</a-checkbox>
                <a-checkbox value="Uninvoiced">Uninvoiced</a-checkbox>
              </a-checkbox-group>
            </a-form-item>
          </a-col>
          <a-col :xs="24" :sm="24" :md="24" class="btns">
            <a-button type="primary" @click="handleSearch">Search</a-button>
            <a-button @click="handleReset">Reset</a-button>
            <a :href="exportUrl" download>
              <a-button type="primary">Export</a-button>
            </a>
          </a-col>
        </a-row>
      </a-form>
    </div>

    <div class="table-wrapper">
      <a-spin :spinning="isLoading">
        <a-table class="payments-table" :data-source="paymentsList" :columns="columns" :scroll="{ x: 1600 }"
          size="small" :pagination="tablePagination" :showSorterTooltip="false" :rowClassName="getPaymentRowClassName"
          @change="handleTableChange">
          <template #headerCell="{ column }">
            <TableTitlePopover v-if="column.titleTip" :title="column.key" :content="column.titleTip" />
            <span v-else>{{ column.key || column.title }}</span>
          </template>


          <template #bodyCell="{ column, record }">
            <template v-if="column.dataIndex === 'affiliatePaymentDate'">
              {{
                record.affiliatePaymentDate
                  ? moment(record.affiliatePaymentDate).format('YYYY-MM-DD')
                  : '-'
              }}
            </template>

            <template v-else-if="column.dataIndex === 'paymentId'">
              <span class="payment-id-cell">
                {{ record.paymentId ?? '-' }}
                <a-tooltip
                  v-if="record.affiliateHeld"
                  placement="top"
                  overlay-class-name="payment-id-tip-tooltip"
                  :overlay-style="{ maxWidth: '360px' }"
                >
                  <template #title>
                    This payment data needs to be fixed manually by tech team.
                  </template>
                  <QuestionCircleFilled style="margin-left: 4px; color: #ff4d4f; font-size: 14px; cursor: pointer;" />
                </a-tooltip>
              </span>
            </template>

            <template v-else-if="
              column.dataIndex === 'paymentAmountWithBonus' ||
              column.dataIndex === 'paymentAmountOriginal' ||
              column.dataIndex === 'financeConfirmReceivedAmountUsd' ||
              column.dataIndex === 'paymentAmountUsdForKol'
            ">
              <span v-if="column.dataIndex === 'paymentAmountWithBonus'">
                {{ record.paymentAmountWithBonus || '-' }}
              </span>
              <span v-else-if="column.dataIndex === 'paymentAmountOriginal'">
                {{ formatCurrency(record.paymentAmountOriginal, record.currency) }}
              </span>
              <span v-else-if="column.dataIndex === 'financeConfirmReceivedAmountUsd'">
                {{ formatCurrency(record.financeConfirmReceivedAmountUsd, 'USD') }}
              </span>
              <span v-else-if="column.dataIndex === 'paymentAmountUsdForKol'">
                {{ formatCurrency(record.paymentAmountUsdForKol, 'USD') }}
              </span>
            </template>

            <template v-else-if="
              column.dataIndex === 'inStatement' ||
              column.dataIndex === 'matchStatus' ||
              column.dataIndex === 'adjust'
            ">
              <span>
                {{
                  typeof record[column.dataIndex] === 'boolean'
                    ? record[column.dataIndex]
                      ? 'Yes'
                      : 'No'
                    : record[column.dataIndex] || '-'
                }}
              </span>
            </template>

            <template v-else-if="column.key === 'Status'">
              <span class="status-tag" :class="record.status
                ? record.status.replace(' ', '-').toLowerCase()
                : ''
                ">
                {{ record.status || '-' }}
              </span>
            </template>

            <template v-else-if="column.key === 'Action'">
              <div class="action-links">
                <a-popconfirm
                  v-if="record.status === 'Pending' && !record.affiliateHeld"
                  :align="{ points: ['br', 'tr'], offset: [-80, 8] }"
                  :overlayClassName="dm-received-confirm-popconfirm"
                  :overlayStyle="{ width: '400px' }"
                  :open="confirmPopPaymentId != null && String(confirmPopPaymentId) === String(record.paymentId)"
                  :icon="false"
                  :okButtonProps="{ size: 'middle' }"
                  :cancelButtonProps="{ size: 'middle' }"
                  :okText="confirmOkText" @openChange="(open) => onConfirmPopOpenChange(open, record)" @confirm="handleConfirmPopConfirm">
                  <template #title>
                    <span class="dm-received-confirm-popconfirm__title">
                      Payment ID: <span>{{ confirmForm.paymentId }}</span> | Payment Date: <span>{{
                        confirmForm.affiliatePaymentDate }}</span>
                    </span>
                  </template>
                  <template #description>
                    <div class="dm-received-confirm-popconfirm__body">
                      <div class="dm-received-confirm-popconfirm__payment-bonus" v-if="record.paymentAmountWithBonus">
                        Payment (incl. bonus): {{ formatCurrency(record.paymentAmountWithBonus, record.currency) }}
                      </div>
                      <div class="dm-received-confirm-popconfirm__ref">
                        Reference Amount USD: {{confirmReferenceLoading ? 'Loading...' : formatCurrency(confirmForm.referenceAmountUsd, 'USD') }}
                      </div>
                      <a-form layout="vertical" class="dm-received-confirm-popconfirm__form">
                        <a-form-item class="dm-received-confirm-popconfirm__finance-item"
                          label="Finance Confirm received amount without VAT (USD)"
                          :validateStatus="confirmErrors.financeAmount ? 'error' : ''"
                          :help="confirmErrors.financeAmount || ''">
                          <a-input v-model:value="confirmForm.financeAmount" prefix="$" @input="onFinanceAmountInput" />
                        </a-form-item>
                        <a-form-item class="dm-received-confirm-popconfirm__rate-item">
                          <div class="dm-received-confirm-popconfirm__rate-label">
                            Received Currency Rate to USD :
                            <span class="dm-received-confirm-popconfirm__rate-value">{{ confirmForm.bankRate }}</span>
                          </div>
                          <div v-if="confirmForm.showWarning" class="dm-received-confirm-popconfirm__rate-warning">
                            <span class="dm-received-confirm-popconfirm__rate-warning-label">Difference: </span>{{
                              formatCurrency(confirmForm.diff, 'USD') }} ({{ confirmForm.diffPercentage }}%)
                          </div>
                        </a-form-item>
                      </a-form>
                    </div>
                  </template>
                  <a href="javascript:;" @click.prevent>Confirm</a>
                </a-popconfirm>
                <span
                  v-else-if="record.status === 'Pending' && record.affiliateHeld"
                  class="action-link-disabled"
                >Confirm</span>
                <a :href="`/#/payments/dm-received/${record.paymentId}`" target="_blank">Details</a>
              </div>
            </template>

            <template v-else-if="column.dataIndex === 'note'">
              <a-input v-model:value="record.note" placeholder="Enter" allow-clear
                @blur="(e) => handleNoteChange(record, e.target?.value)"
                @pressEnter="(e) => handleNoteChange(record, e.target?.value)" />
            </template>
            <template v-else-if="column.dataIndex === 'paymentBalance'">
              {{ record.paymentBalance != null && record.paymentBalance !== '' ? record.paymentBalance : '-' }}
            </template>
            <template v-else-if="column.dataIndex === 'paymentBalanceCarriedForward'">
              {{ record.paymentBalanceCarriedForward != null && record.paymentBalanceCarriedForward !== '' ? record.paymentBalanceCarriedForward : '-' }}
            </template>
            <template v-else-if="column.dataIndex === 'affiliatePaymentAmount'">
              <div v-if="record.status === 'Uninvoiced'">
                <div>
                  {{ record['affiliatePaymentAmount'] }}
                </div>
                <div v-if="record['paymentInvoicedAmount']">
                  · Invoice: {{ record['paymentInvoicedAmount'] }}
                </div>
                <div class="uninvoiced-amount" v-if="record['paymentUninvoicedAmount']">
                  · Uninvoiced: {{ record['paymentUninvoicedAmount'] }}
                </div>
              </div>
              <div v-else>
                {{ record[column.dataIndex] }}
              </div>
            </template>
            <template v-else>
              {{ record[column.dataIndex] }}
            </template>
          </template>
        </a-table>
      </a-spin>
    </div>

  </div>
</template>

<script setup>
import moment from 'moment-timezone'
import { message } from 'ant-design-vue'
import { apiDomain } from '../../../config'
import payments from '../../server/payments'
import { computed, onMounted, ref, watch } from 'vue'
import advertiser from '../../server/advertiser'
import { QuestionCircleFilled } from '@ant-design/icons-vue'
import TableTitlePopover from '../../components/tableTitlePopover.vue'

const isLoading = ref(false)

/** 当前打开 Confirm Popconfirm 的 paymentId */
const confirmPopPaymentId = ref(null)
const confirmReferenceLoading = ref(false)
const confirmForm = ref({
  diff: '',
  bankRate: '',
  paymentId: '',
  financeAmount: '',
  showWarning: false,
  diffPercentage: '',
  referenceAmountUsd: '',
  affiliatePaymentDate: '',
})
const confirmErrors = ref({
  financeAmount: '',
})

const emptyConfirmForm = () => ({
  diff: '',
  bankRate: '',
  paymentId: '',
  financeAmount: '',
  showWarning: false,
  diffPercentage: '',
  referenceAmountUsd: '',
  affiliatePaymentDate: '',
})

const confirmOkText = computed(() =>
  confirmForm.value.showWarning ? 'Confirm Again' : 'Confirm'
)

const filter = ref({
  affiliateId: undefined,
  statusList: ['Pending'],
})
const filterDate = ref([])

/** 超过两年前 / 晚于今天的日期不可选 */
function disabledDate(current) {
  if (!current) return false
  const m = moment(current)
  const minStart = moment().subtract(2, 'years').startOf('day')
  const maxEnd = moment().endOf('day')
  return m.isBefore(minStart, 'day') || m.isAfter(maxEnd, 'day')
}

const paymentsList = ref([])
const pagination = ref({
  current: 1,
  pageSize: 50,
  total: 0,
  sort: '-affiliatePaymentDate',
})

const affiliateOptions = ref([])

const columns = ref([
  {
    dataIndex: 'affiliatePaymentDate',
    key: 'Payment Date',
    width: 130,
    sorter: true,
    sortOrder: 'descend',
  },
  {
    dataIndex: 'paymentId',
    key: 'Payment ID',
    width: 110,
  },
  {
    dataIndex: 'affiliatePaymentId',
    key: 'A.Payment ID',
    width: 150,
  },
  {
    dataIndex: 'affiliate',
    key: 'Affiliate',
    width: 150,
    sorter: true,
  },
  {
    dataIndex: 'currency',
    key: 'Currency',
    width: 110,
    titleTip: 'Currency used by the affiliate for payment.',
  },
  {
    dataIndex: 'paymentAmountWithBonus',
    key: 'Payment (incl. bonus)',
    width: 160,
  },
  {
    dataIndex: 'affiliatePaymentAmount',
    key: 'Payment Amount Original',
    width: 160,
    sorter: true,
    titleTip:
      'Original payment amount from the affiliate\'s Payment History.',
  },
  {
    dataIndex: 'currencyExchangeRate',
    key: 'Bank Received Currency Rate',
    width: 160,
    titleTip: 'Currency rate (5 decimals) calculated by finance after payment confirmation, based on USD, used to recompute all commissions.',
  },
  {
    dataIndex: 'financeConfirmReceivedAmountUsd',
    key: 'Finance Confirm Received Amount USD',
    width: 200,
    titleTip: 'Net amount received manually entered after finance confirmation.',
  },
  {
    dataIndex: 'paymentAmountUsdForKol',
    key: 'Payment Amount for KOL USD',
    width: 200,
    sorter: true,
    titleTip: 'Sum of payment amounts to influencers after applying the Currency Rate and CPA ratio to each commission.',
  },
  {
    dataIndex: 'inStatement',
    key: 'In Statement',
    width: 140,
    titleTip: 'Whether the amount matches between Payment Detail and Payment History.',
  },
  {
    dataIndex: 'matchStatus',
    key: 'Match Status',
    width: 130,
    titleTip: 'Daily updated status: whether total amounts match across Payment History, Payment Invoice, and Payment Detail Commission.',
  },
  {
    dataIndex: 'adjust',
    key: 'Is Adjust?',
    width: 120,
    titleTip: 'Indicates discrepancies, if yes, you can check the Commission Details, where declined commissions will be highlighted.',
  },
  {
    dataIndex: 'status',
    key: 'Status',
    width: 110,
  },
  {
    dataIndex: 'paymentBalance',
    key: 'PaymentBalance',
    width: 160,
    titleTip:'This amount could not be matched to an order in the current bill and will be matched in the next billing cycle.'
  },
  {
    dataIndex: 'paymentBalanceCarriedForward',
    key: 'PaymentBalanceCarriedForward',
    width: 160,
    titleTip:'This amount was carried over from the previous bill and was not matched to an order in that billing cycle.'
  },
  {
    dataIndex: 'note',
    key: 'Note',
    width: 160,
  },
  {
    key: 'Action',
    width: 140,
    fixed: 'right',
  },
])

// 根据当前排序字段高亮所有可排序列的 sortOrder
watch(
  () => pagination.value.sort,
  (val) => {
    columns.value.forEach((col) => {
      if (!col.sorter || !col.dataIndex) return
      if (val === `-${col.dataIndex}`) {
        col.sortOrder = 'descend'
      } else if (val === `+${col.dataIndex}` || val === col.dataIndex) {
        col.sortOrder = 'ascend'
      } else {
        col.sortOrder = null
      }
    })
  },
  { immediate: true }
)

const tablePagination = computed(() => ({
  current: pagination.value.current,
  pageSize: pagination.value.pageSize,
  total: pagination.value.total,
  showSizeChanger: true,
}))

const buildParams = () => {
  const params = {
    type: 'dmReceived',
    page: pagination.value.current,
    size: pagination.value.pageSize,
    status: filter.value.statusList.length > 2 ? null : filter.value.statusList.join(','),
    affiliateId: filter.value.affiliateId ? filter.value.affiliateId : null,
  }

  if (filterDate.value && filterDate.value.length === 2) {
    params.paymentStartTime = filterDate.value[0]?.valueOf()
    params.paymentEndTime = filterDate.value[1]?.valueOf()
  }

  Object.keys(params).forEach((key) => {
    if (
      params[key] === null ||
      params[key] === undefined ||
      params[key] === ''
    ) {
      delete params[key]
    }
  })

  // 排序字段：优先使用 pagination.sort，没有则使用默认按支付日期降序
  if (pagination.value.sort) {
    params.sort = encodeURIComponent(pagination.value.sort)
  } else {
    params.sort = '-affiliatePaymentDate'
  }

  return params
}

const fetchList = () => {
  isLoading.value = true

  const params = buildParams()

  payments
    .getList(params)
    .then((res) => {
      isLoading.value = false

      const list = res?.list || res?.records || res?.data || []
      paymentsList.value = Array.isArray(list) ? list : []
      pagination.value.total =
        res?.total || res?.dataTotal || paymentsList.value.length
    })
    .catch(() => {
      isLoading.value = false
    })
}

const handleSearch = () => {
  pagination.value.current = 1
  fetchList()
}

const handleReset = () => {
  filter.value = {
    affiliateId: undefined,
    statusList: ['Pending'],
  }
  filterDate.value = []
  pagination.value.current = 1
  pagination.value.sort = '-affiliatePaymentDate'
  fetchList()
}

const handleTableChange = (pager, fitlers, sorter) => {
  // 判断是否为分页器变化
  const isPagerChange = pager?.current !== pagination.value.current || pager?.pageSize !== pagination.value.pageSize

  if (isPagerChange) {
    if (pager?.current) {
      pagination.value.current = pager.current
    }
    if (pager?.pageSize) {
      pagination.value.pageSize = pager.pageSize
    }
  } else {
    let sortField = ''

    if (sorter && sorter.field) {
      let defaultSort = '-affiliatePaymentDate'
      // 字段映射
      switch (sorter.field) {
        case 'pendingdate':
        case 'paymentDate':
        case 'affiliatePaymentDate':
          sortField = 'affiliatePaymentDate'
          break
        case 'affiliate':
          sortField = 'affiliate'
          break
        case 'paymentAmountOriginal':
        case 'affiliatePaymentAmount':
        case 'paymentAmountWithVat':
          sortField = 'affiliatePaymentAmount'
          break
        case 'financeConfirmReceivedAmountUsd':
          sortField = 'financeConfirmReceivedAmountUsd'
          break
        case 'paymentAmountUsdForKol':
          sortField = 'paymentAmountUsdForKol'
          break
        default:
          sortField = ''
      }

      if (sortField) {
        if (pagination.value.sort.endsWith(sortField)) {
          pagination.value.sort = (pagination.value.sort.startsWith('-') ? '+' : '-') + sortField
        } else {
          pagination.value.sort = `-${sortField}`
        }
      } else {
        pagination.value.sort = defaultSort
      }
    }
  }

  fetchList()
}

const onConfirmPopOpenChange = async (open, record) => {
  if (!open) {
    if (financeAmountTimer) {
      clearTimeout(financeAmountTimer)
      financeAmountTimer = null
    }
    confirmPopPaymentId.value = null
    confirmReferenceLoading.value = false
    confirmErrors.value.financeAmount = ''
    confirmForm.value = emptyConfirmForm()
    return
  }

  if (record?.affiliateHeld) {
    confirmPopPaymentId.value = null
    return
  }

  confirmPopPaymentId.value = record.paymentId
  confirmReferenceLoading.value = true
  confirmForm.value = {
    ...emptyConfirmForm(),
    paymentId: record.paymentId || '',
    affiliatePaymentDate: record.affiliatePaymentDate
      ? moment(record.affiliatePaymentDate).format('YYYY-MM-DD')
      : '',
    financeAmount:
      record.financeConfirmReceivedAmountUsd != null
        ? String(record.financeConfirmReceivedAmountUsd)
        : '',
    bankRate:
      record.currencyExchangeRate != null ? String(record.currencyExchangeRate) : '',
  }
  confirmErrors.value.financeAmount = ''

  const referenceAmount = await payments.getDmReceivedConfirmReferenceAmount(record.paymentId)
  confirmReferenceLoading.value = false
  if (!referenceAmount.success) {
    message.error(referenceAmount.message || 'Failed to get reference amount')
    confirmPopPaymentId.value = null
    confirmForm.value = emptyConfirmForm()
    return
  }

  confirmForm.value.referenceAmountUsd = referenceAmount.data.referenceAmountUsd
  confirmReferenceLoading.value = false
}

const handleNoteChange = (record, value) => {
  record.note = value
  payments.addNote({ type: 'dmReceived', paymentId: record.paymentId, note: value }).then(() => {
    message.success('Note saved')
  })
}

const validateFinanceAmount = () => {
  const raw = String(confirmForm.value.financeAmount ?? '').trim()
  const num = Number(raw)

  if (!raw || Number.isNaN(num) || num <= 0) {
    confirmErrors.value.financeAmount = 'Please enter a number greater than 0.'
    return null
  }

  confirmErrors.value.financeAmount = ''
  return num
}

const getExchangeRate = () => {
  const financeAmount = validateFinanceAmount()
  if (financeAmount == null) return

  payments
    .exchangeRate({
      paymentId: confirmForm.value.paymentId,
      financeConfirmReceivedAmountUsd: financeAmount,
    })
    .then((res) => {
      confirmForm.value.bankRate = res.currencyExchangeRate
    })
}

let financeAmountTimer = null
const onFinanceAmountInput = () => {
  confirmForm.value.diff = ''
  confirmForm.value.showWarning = false
  confirmForm.value.diffPercentage = ''
  if (financeAmountTimer) {
    clearTimeout(financeAmountTimer)
  }
  financeAmountTimer = setTimeout(() => {
    getExchangeRate()
  }, 100)
}

/**
 * Popconfirm OK：返回 Promise；校验未通过或需二次确认（>5%）时 reject，弹层保持打开。
 */
const handleConfirmPopConfirm = () => {
  if (confirmReferenceLoading.value) {
    return Promise.reject(new Error('loading'))
  }

  const financeNum = validateFinanceAmount()
  if (financeNum == null) {
    return Promise.reject(new Error('validation'))
  }

  const refAmt = Number(confirmForm.value.referenceAmountUsd)
  if (!confirmForm.value.showWarning && refAmt) {
    const diff = financeNum - refAmt
    if (Math.abs(diff / refAmt * 100) > 5) {
      confirmForm.value.diff = diff
      confirmForm.value.diffPercentage = ((diff / refAmt) * 100).toFixed(2)
      confirmForm.value.showWarning = true
      return Promise.reject(new Error('threshold'))
    }
  }

  return payments
    .saveExchangeRate({
      paymentId: confirmForm.value.paymentId,
      financeConfirmReceivedAmountUsd: confirmForm.value.financeAmount,
      exchangeRate: confirmForm.value.bankRate,
    })
    .then(() => {
      message.success('Payment confirmed')
      fetchList()
      if (financeAmountTimer) {
        clearTimeout(financeAmountTimer)
        financeAmountTimer = null
      }
      confirmPopPaymentId.value = null
      confirmErrors.value.financeAmount = ''
      confirmForm.value = emptyConfirmForm()
    })
    .catch(() => {
      message.error('Failed to confirm')
      return Promise.reject(new Error('api'))
    })
}

const formatCurrency = (value, currency) => {
  if (value === null || value === undefined || value === '') {
    return '-'
  }

  const num = Number(value)
  if (Number.isNaN(num)) {
    return String(value)
  }

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num)
  } catch (e) {
    return `${currency || ''} ${num.toFixed(2)}`.trim()
  }
}

const getPaymentRowClassName = (record) =>
  String(confirmPopPaymentId.value || '') === String(record.paymentId || '')
    ? 'row-highlight-by-confirm-payment'
    : ''

const exportUrl = computed(() => {
  const params = []

  if (filterDate.value && filterDate.value.length === 2) {
    params.push(`paymentStartTime=${filterDate.value[0]?.valueOf()}`)
    params.push(`paymentEndTime=${filterDate.value[1]?.valueOf()}`)
  }
  if (filter.value.affiliateId) {
    params.push(`affiliateId=${filter.value.affiliateId}`)
  }
  if (filter.value.statusList?.length == 1) {
    params.push(`status=${filter.value.statusList.join(',')}`)
  }

  const query = params.length ? `?${params.join('&')}` : ''

  return `https://${apiDomain}/admin/influencer-marketing/v1/finance/dm-received/payments/export${query}`
})

onMounted(() => {
  fetchList()
  advertiser.getAffiliateList().then((res) => {
    affiliateOptions.value = res.data.map((item) => ({
      label: item.name,
      value: item.id,
    }))
  })
})
</script>

<style scoped>
.table-wrapper :deep(.ant-select-dropdown) {
  width: auto !important;
  /* 关键：宽度自动 */
  min-width: 100%;
  /* 可选：最小宽度和输入框一致，避免过窄 */
  max-width: none !important;
  /* 取消最大宽度限制 */
}

.status-checkbox-group {
  flex-wrap: nowrap;
}

.payments-table :deep(.row-highlight-by-confirm-payment td) {
  background: #f8cfbf !important;
}
.payment-id-cell {
  display: inline-flex;
  align-items: center;
}
.action-link-disabled {
  color: rgba(0, 0, 0, 0.25);
  cursor: not-allowed;
  user-select: none;
}
.uninvoiced-amount{
  color: red;
}
.uninvoiced{
  color: red;
}
</style>
<style lang="less">
.payment-id-tip-tooltip {
  .ant-tooltip-inner {
    color: rgba(0, 0, 0, 0.88);
    background-color: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .ant-tooltip-arrow {
    --antd-arrow-background-color: #fff;
  }

  .ant-tooltip-arrow::before,
  .ant-tooltip-arrow-content {
    background-color: #fff;
  }
}

.dm-received-confirm-popconfirm .ant-popconfirm-message-title {
  margin: 0;
  font-weight: 600;
  font-size: 14px;
  text-align: center;
  line-height: 1.4;
}

.dm-received-confirm-popconfirm__title span {
  text-align: center;
  font-weight: 600;
}

.dm-received-confirm-popconfirm__loading {
  padding: 12px 0;
  color: #999;
  text-align: center;
}

.dm-received-confirm-popconfirm__payment-bonus {
  color: #999;
  margin-top: -10px;
}

.dm-received-confirm-popconfirm__ref {
  color: #db2828;
  margin-bottom: 12px;
  font-size: 13px;
}

.dm-received-confirm-popconfirm__form {
  .dm-received-confirm-popconfirm__finance-item {
    margin-bottom: 12px;
    .ant-form-item-row {
      gap: 12px;
      display: flex;
      align-items: center;
      flex-direction: row;
    }

    .ant-form-item-label {
      max-width: 200px;
      margin-bottom: 0;
      padding-bottom: 0;
      line-height: 20px;
    }

    .ant-form-item-control {
      flex: 1;
      margin-bottom: 0;
      min-height: 32px;
    }

    .ant-input-affix-wrapper {
      height: 36px;
      padding-top: 4px;
      padding-bottom: 4px;
    }

    .ant-form-item-explain-error {
      margin-top: 4px;
      line-height: 14px;
    }
  }

  .ant-form-item-label > label {
    font-weight: 500;
  }

  .dm-received-confirm-popconfirm__rate-item {
    margin-bottom: 0;
  }

  .dm-received-confirm-popconfirm__rate-label {
    display: flex;
    font-size: 13px;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 4px;
  }

  .dm-received-confirm-popconfirm__rate-value {
    font-weight: 700;
    margin-left: 4px;
  }

  .dm-received-confirm-popconfirm__rate-warning {
    color: #ff4d4f;
    font-size: 13px;
    margin-top: 6px;
    font-weight: 500;
  }

  .dm-received-confirm-popconfirm__rate-warning-label {
    font-weight: 600;
  }
}
</style>
